import { getAccessToken, ensureFreshAccessToken } from './authStore';
import { safeGetSession } from './supabase';

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL as string | undefined;

if (!apiBaseUrl) {
  throw new Error(
    'Missing REACT_APP_API_BASE_URL. Set it to your API Gateway base URL, e.g. https://.../dev/api'
  );
}

export interface GoogleGmailStatus {
  connected: boolean;
  configured: boolean;
  email?: string | null;
  connectedAt?: string | null;
  message?: string;
}

export interface GmailThreadDto {
  id: string;
  leadId: string;
  gmailThreadId: string;
  participantEmail: string;
  subject?: string | null;
  snippet?: string | null;
  lastMessageAt?: string | null;
  linkedAt: string;
  updatedAt: string;
}

export interface GmailMessageDto {
  id: string;
  threadId: string;
  gmailMessageId: string;
  direction: 'inbound' | 'outbound';
  fromEmail?: string | null;
  toEmails: string[];
  subject?: string | null;
  bodyText?: string | null;
  sentAt?: string | null;
}

export interface GmailThreadPayload {
  thread: GmailThreadDto | null;
  messages: GmailMessageDto[];
}

/** Deep-link into the Gmail web UI for a known thread (no inbox read required). */
export function gmailThreadWebUrl(gmailThreadId: string): string {
  return `https://mail.google.com/mail/u/0/#all/${encodeURIComponent(gmailThreadId)}`;
}

async function getJwtOrThrow(): Promise<string> {
  const token =
    (await ensureFreshAccessToken()) ||
    getAccessToken() ||
    (await safeGetSession(4000)).data.session?.access_token ||
    undefined;
  if (!token) {
    throw new Error('Ta session a expiré. Reconnecte-toi et réessaie.');
  }
  return token;
}

async function jsonFetch<T>(input: RequestInfo, init?: RequestInit): Promise<T> {
  const token = await getJwtOrThrow();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((init?.headers as Record<string, string>) || {}),
  };
  headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(input, { ...init, headers });

  if (!res.ok) {
    let message = `Request failed with status ${res.status}`;
    let code: string | undefined;
    try {
      const body = await res.json();
      if (body?.message) message = body.message;
      if (typeof body?.code === 'string') code = body.code;
    } catch {
      // ignore
    }
    const err = new Error(message) as Error & { status?: number; code?: string };
    err.status = res.status;
    err.code = code || (res.status === 429 ? 'RATE_LIMIT' : undefined);
    throw err;
  }

  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const googleGmailService = {
  async getStatus(): Promise<GoogleGmailStatus> {
    return jsonFetch<GoogleGmailStatus>(`${apiBaseUrl}/integrations/google/gmail/status`);
  },

  async getConnectUrl(): Promise<string> {
    const data = await jsonFetch<{ url: string }>(
      `${apiBaseUrl}/integrations/google/gmail/connect`
    );
    if (!data?.url) throw new Error('Missing connect URL');
    return data.url;
  },

  async disconnect(): Promise<void> {
    await jsonFetch(`${apiBaseUrl}/integrations/google/gmail/disconnect`, {
      method: 'POST',
      body: '{}',
    });
  },

  /** Local outbound history for this lead (never reads Gmail inbox). */
  async getThread(leadId: string): Promise<GmailThreadPayload> {
    const q = new URLSearchParams({ leadId });
    return jsonFetch<GmailThreadPayload>(
      `${apiBaseUrl}/integrations/google/gmail/thread?${q.toString()}`
    );
  },

  async unlink(leadId: string): Promise<void> {
    await jsonFetch(`${apiBaseUrl}/integrations/google/gmail/unlink`, {
      method: 'POST',
      body: JSON.stringify({ leadId }),
    });
  },

  async send(params: {
    leadId: string;
    to: string;
    subject: string;
    body: string;
    bodyHtml?: string;
    threadId?: string;
    fileIds?: string[];
  }): Promise<GmailThreadPayload & { ok: boolean; gmailMessageId?: string; gmailThreadId?: string }> {
    return jsonFetch(`${apiBaseUrl}/integrations/google/gmail/send`, {
      method: 'POST',
      body: JSON.stringify(params),
    });
  },
};

/** User-facing i18n key for known Gmail API errors; null = use generic fallback. */
export function gmailErrorMessageKey(err: unknown): string | null {
  const e = err as { code?: string; status?: number } | null;
  if (e?.code === 'RATE_LIMIT' || e?.status === 429) return 'messages.gmailError_RATE_LIMIT';
  if (e?.code === 'TOO_MANY_ATTACHMENTS') return 'messages.gmailError_TOO_MANY_ATTACHMENTS';
  if (e?.code === 'ATTACHMENTS_TOO_LARGE') return 'messages.gmailError_ATTACHMENTS_TOO_LARGE';
  if (e?.code === 'ATTACHMENT_DRIVE_LINK') return 'messages.gmailError_ATTACHMENT_DRIVE_LINK';
  if (e?.code === 'ATTACHMENT_NOT_FOUND' || e?.code === 'ATTACHMENT_FORBIDDEN' || e?.code === 'INVALID_ATTACHMENT') {
    return 'messages.gmailError_ATTACHMENT_INVALID';
  }
  return null;
}
